import { useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Check, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { PostLoginNavigate } from '@/features/auth/components/post-login-navigate'
import { useAccountStore } from '@/stores/account-store'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { PasswordInput } from '@/components/password-input'
import {
  PASSWORD_MISMATCH_MESSAGE,
  PASSWORD_RULES,
  isPasswordCompliant,
} from '@/services/auth/password-policy'
import { trackEvent } from '@/services/analytics/use-analytics'
import { EVENTS } from '@/services/analytics/events'
import { authErrorName } from '@/services/analytics/auth-error-name'
import { setMilestone } from '@/services/analytics/milestones'

export function RegisterPage() {
  const navigate = useNavigate()
  const session = useAccountStore((s) => s.session)
  const isAuthenticating = useAccountStore((s) => s.isAuthenticating)
  const authError = useAccountStore((s) => s.authError)

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [givenName, setGivenName] = useState('')
  const [familyName, setFamilyName] = useState('')

  if (session) {
    return <PostLoginNavigate />
  }

  // PASSWORD_RULES mirrors the Cognito pool policy, so the form refuses exactly
  // what the server would refuse instead of bouncing the user off a 400.
  const mismatch = confirmPassword.length > 0 && confirmPassword !== password
  const canSubmit = isPasswordCompliant(password) && confirmPassword === password

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    useAccountStore.getState().clearAuthError()
    trackEvent(EVENTS.SIGNUP_SUBMITTED, {})
    try {
      await useAccountStore.getState().register({ email, password, givenName, familyName })
      trackEvent(EVENTS.SIGNUP_COMPLETED, {})
      // Targets the `replay-new-users` flag. Set on the anonymous person here;
      // PostHog merges it into the account when the app identifies them.
      setMilestone('signed_up_at', { forFlags: true })
      navigate('/verify-email', { replace: true, state: { email } })
    } catch (err) {
      // The message is already shown from the store; analytics gets only the name.
      trackEvent(EVENTS.SIGNUP_FAILED, { error_name: authErrorName(err) })
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground">Create Account</h1>
          <p className="mt-1 text-sm text-muted-foreground">Sign up for Edge to Trade</p>
        </div>

        {authError && (
          <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {authError}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <label htmlFor="givenName" className="text-sm font-medium text-foreground">
                First name
              </label>
              <Input
                id="givenName"
                value={givenName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGivenName(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="familyName" className="text-sm font-medium text-foreground">
                Last name
              </label>
              <Input
                id="familyName"
                value={familyName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFamilyName(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="email" className="text-sm font-medium text-foreground">
              Email
            </label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              autoComplete="email"
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="password" className="text-sm font-medium text-foreground">
              Password
            </label>
            <PasswordInput
              id="password"
              value={password}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
              placeholder="Password"
              autoComplete="new-password"
            />
          </div>

          {password.length > 0 && (
            <ul className="space-y-1" aria-label="Password requirements">
              {PASSWORD_RULES.map((rule) => {
                const ok = rule.test(password)
                return (
                  <li
                    key={rule.label}
                    className={cn(
                      'flex items-center gap-2 text-xs',
                      ok ? 'text-emerald-600 dark:text-emerald-500' : 'text-muted-foreground',
                    )}
                  >
                    {ok ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                    {rule.label}
                  </li>
                )
              })}
            </ul>
          )}

          <div className="space-y-2">
            <label htmlFor="confirmPassword" className="text-sm font-medium text-foreground">
              Confirm password
            </label>
            <PasswordInput
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setConfirmPassword(e.target.value)
              }
              placeholder="Confirm password"
              autoComplete="new-password"
              aria-invalid={mismatch || undefined}
              aria-describedby={mismatch ? 'confirmPassword-error' : undefined}
            />
            {mismatch && (
              <p id="confirmPassword-error" className="text-xs text-destructive">
                {PASSWORD_MISMATCH_MESSAGE}
              </p>
            )}
          </div>

          <Button type="submit" className="w-full" disabled={isAuthenticating || !canSubmit}>
            {isAuthenticating ? 'Creating account...' : 'Create account'}
          </Button>
        </form>

        <p className="text-center text-sm text-muted-foreground">
          Already have an account?{' '}
          <Link to="/login" className="text-primary hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  )
}
